import {
  OverlayLabelAnchor,
  OverlayLabelPlacement,
  type OverlayLabelPrimitive,
  type OverlayPrimitives,
  renderReportHeatmapBundle,
  ReportComponentOverlayMode,
  ReportLabelMode,
  ReportOverlayMode,
} from '@asset-viz/reporting/reportHeatmap'
import {
  Alert,
  Button,
  Card,
  Image as AntImage,
  InputNumber,
  message,
  Progress,
  Select,
  Space,
  Typography,
} from 'antd'
import {
  determineDefaultColorScaleThresholdNonCdf,
  getCdfDisplayMetadata,
  useCdfDataFetcher,
} from 'features/dataLayers'
import { gcrToBinSelection } from 'features/dataLayers/load/useCdfDataGenerator'
import { useRolodexEdit } from 'features/fde/rolodexAPI'
import { useGeckoLocale } from 'features/locale'
import { useRolodexFileLS, useRolodexSignedURLs } from 'features/rolodex'
import {
  AssetDrawings,
  useAssetDrawings,
} from 'pages/reporting/reports/raut/hooks/useAssetDrawings'
import { RautInspectionAssetDrawingsData } from 'pages/reporting/reports/raut/rautReportData'
import { useRautReportOps } from 'pages/reporting/reports/raut/RautReportProvider'
import {
  buildRautAssetDrawingBundlePaths,
  createRautAssetDrawingChunkKey,
  serializeRautAssetDrawingDescriptor,
} from 'pages/reporting/reports/raut/utils/assetDrawingBundleStorage'
import { useUnitSystem } from 'pages/reporting/shared/contexts/UnitSystemContext'
import React, { FC, useCallback, useMemo, useState } from 'react'
import styled from 'styled-components'
import {
  GeckoComponent,
  GeckoComponentDataFrame,
  GeckoComponentDefaults,
  GeckoComponentModel,
  GeckoComponentModelType,
  GeckoComponentRect,
  GeckoEllipsoidalHead,
  GeckoHemisphericalHead,
  GeckoMarkupLayer,
  GeckoMeasurement,
  GeckoMeasurementRange,
  GeckoTorisphericalHead,
} from 'types'

const AntText = Typography.Text
const ELLIPSOIDAL_HEAD_BUFFER_FACTOR = 0.2
const DEFAULT_DISTANCE_PER_PAGE = 20
const BUNDLE_OUTPUT_WIDTH_PX = 2000
const SCAN_LABEL_SUFFIX_REGEX = /(S[-_]*\d+)\s*$/
const SCAN_PLAN_FALLBACK_LINE_COLOR = '#800080'

type HeatmapOverlaySelection = 'scanPlan' | 'assetGeometry' | 'none'

const HEATMAP_OVERLAY_OPTIONS: Array<{
  label: string
  value: HeatmapOverlaySelection
}> = [
  { label: 'Asset geometry', value: 'assetGeometry' },
  { label: 'Asset geometry + scan plan', value: 'scanPlan' },
  { label: 'None', value: 'none' },
]

const StatusCard = styled(Card)`
  margin-bottom: 20px;
`

const ProgressInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin: 20px 0;
`

const ButtonContainer = styled(Space)`
  margin-bottom: 16px;
`

const ZoomControls = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 16px;
`

const PreviewRow = styled.div`
  display: flex;
  gap: 12px;
  align-items: flex-start;
  width: 100%;
`

interface CaptureStatus {
  isCapturing: boolean
  message: string
}

function truncateAssetDrawingScanLabel(text: string) {
  return text.match(SCAN_LABEL_SUFFIX_REGEX)?.[1] ?? text
}

export interface AssetDrawingTabContentProps {
  inspectionId: string
  component: GeckoComponent
  componentModel: GeckoComponentModel
  thicknessCDF: GeckoComponentDataFrame | undefined
  markupLayers: readonly GeckoMarkupLayer[]
  metadataEntryId: string
  onDrawingFinish: (
    inspectionId: string,
    inspectionDrawings: RautInspectionAssetDrawingsData
  ) => Promise<void>
  onCaptureStatusChange?: (status: CaptureStatus) => void
  isLocked: boolean
  nominal: GeckoMeasurement | undefined
  componentDefaults: GeckoComponentDefaults | undefined
}

function toCartesianBounds(
  componentModel: GeckoComponentModel
): GeckoComponentRect {
  const bounds = componentModel.componentBounds

  if (componentModel.type === GeckoComponentModelType.EllipsoidalHead) {
    const model = componentModel as GeckoEllipsoidalHead
    const radius = Math.max(model.radius.num, model.minorRadius.num)
    return GeckoComponentRect.CreateFromXYs(
      bounds.componentID,
      -radius - radius * ELLIPSOIDAL_HEAD_BUFFER_FACTOR,
      -radius - radius * ELLIPSOIDAL_HEAD_BUFFER_FACTOR,
      radius + radius * ELLIPSOIDAL_HEAD_BUFFER_FACTOR,
      radius + radius * ELLIPSOIDAL_HEAD_BUFFER_FACTOR,
      bounds.y.unit,
      bounds.y.unit
    )
  }

  if (componentModel.type === GeckoComponentModelType.TorisphericalHead) {
    const model = componentModel as GeckoTorisphericalHead
    const radius = model.crownRadius.num
    return GeckoComponentRect.CreateFromXYs(
      bounds.componentID,
      -radius,
      -radius,
      radius,
      radius,
      bounds.y.unit,
      bounds.y.unit
    )
  }

  if (componentModel.type === GeckoComponentModelType.HemisphericalHead) {
    const model = componentModel as GeckoHemisphericalHead
    const radius = model.radius.num
    return GeckoComponentRect.CreateFromXYs(
      bounds.componentID,
      -radius,
      -radius,
      radius,
      radius,
      bounds.y.unit,
      bounds.y.unit
    )
  }

  return bounds
}

function getIntersectingScanBounds(
  scans: readonly GeckoComponentRect[],
  chunkStart: number,
  chunkEnd: number,
  unit: GeckoMeasurement['unit']
) {
  let minScanStart: number | undefined
  let maxScanEnd: number | undefined

  for (const scan of scans) {
    const scanStart = scan.start.y.toNumber(unit)
    const scanEnd = scan.end.y.toNumber(unit)

    if (scanStart >= chunkEnd || scanEnd <= chunkStart) {
      continue
    }

    minScanStart =
      minScanStart === undefined ? scanStart : Math.min(minScanStart, scanStart)
    maxScanEnd =
      maxScanEnd === undefined ? scanEnd : Math.max(maxScanEnd, scanEnd)
  }

  if (minScanStart === undefined || maxScanEnd === undefined) {
    return undefined
  }

  return {
    minScanStart,
    maxScanEnd,
  }
}

function hasFiniteValues(rawValues: Readonly<Float32Array>) {
  return rawValues.some(Number.isFinite)
}

function isReportMarkupLayer(markupLayer: GeckoMarkupLayer) {
  return markupLayer?.data?.path?.[0] === 'Report'
}

function isScanOverlayLayerName(name: string) {
  return name === 'Scan Overlay' || name === 'Weld Scan Overlay'
}

function isScanLabelLayerName(name: string) {
  return name === 'Scan Labels' || name === 'Weld Scan Labels'
}

function appendScanPlanRectLines(
  scanRect: GeckoComponentRect,
  coordinateBounds: GeckoComponentRect,
  strokeStyle: string,
  linePrimitives: OverlayPrimitives['linePrimitives']
) {
  const xMin = scanRect.x.min.toNumber(coordinateBounds.x.unit)
  const xMax = scanRect.x.max.toNumber(coordinateBounds.x.unit)
  const yMin = scanRect.y.min.toNumber(coordinateBounds.y.unit)
  const yMax = scanRect.y.max.toNumber(coordinateBounds.y.unit)

  linePrimitives.push(
    {
      x1: xMin,
      y1: yMin,
      x2: xMax,
      y2: yMin,
      strokeStyle,
      lineWidth: 2,
    },
    {
      x1: xMax,
      y1: yMin,
      x2: xMax,
      y2: yMax,
      strokeStyle,
      lineWidth: 2,
    },
    {
      x1: xMax,
      y1: yMax,
      x2: xMin,
      y2: yMax,
      strokeStyle,
      lineWidth: 2,
    },
    {
      x1: xMin,
      y1: yMax,
      x2: xMin,
      y2: yMin,
      strokeStyle,
      lineWidth: 2,
    }
  )
}

function isPointInsideBounds(x: number, y: number, bounds: GeckoComponentRect) {
  const xMin = bounds.x.min.toNumber(bounds.x.unit)
  const xMax = bounds.x.max.toNumber(bounds.x.unit)
  const yMin = bounds.y.min.toNumber(bounds.y.unit)
  const yMax = bounds.y.max.toNumber(bounds.y.unit)

  return x >= xMin && x <= xMax && y >= yMin && y <= yMax
}

function createScanPlanOverlayLabel(
  label: GeckoMarkupLayer['data']['labelPrimitives'][number],
  bounds: GeckoComponentRect
): OverlayLabelPrimitive | undefined {
  const x = label.coords.x.toNumber(bounds.x.unit)
  const y = label.coords.y.toNumber(bounds.y.unit)

  if (!isPointInsideBounds(x, y, bounds)) {
    return undefined
  }

  return {
    x,
    y,
    lines: [truncateAssetDrawingScanLabel(label.text)],
    anchor: resolveScanPlanOverlayLabelAnchor(label.anchor),
    placement: OverlayLabelPlacement.Anchored,
    priority: 1,
    font: 'bold 12px Arial',
    lineHeight: 14,
  }
}

function resolveScanPlanOverlayLabelAnchor(
  anchor: GeckoMarkupLayer['data']['labelPrimitives'][number]['anchor']
): OverlayLabelPrimitive['anchor'] {
  return isOverlayLabelAnchor(anchor) ? anchor : OverlayLabelAnchor.Center
}

function isOverlayLabelAnchor(anchor: unknown): anchor is OverlayLabelAnchor {
  return Object.values(OverlayLabelAnchor).includes(
    anchor as OverlayLabelAnchor
  )
}

function buildScanPlanOverlayPrimitives(
  markupLayers: readonly GeckoMarkupLayer[],
  bounds: GeckoComponentRect
): OverlayPrimitives {
  const linePrimitives: OverlayPrimitives['linePrimitives'] = []
  const labelPrimitives: OverlayPrimitives['labelPrimitives'] = []

  markupLayers.filter(isReportMarkupLayer).forEach((markupLayer) => {
    if (isScanOverlayLayerName(markupLayer.data.name)) {
      const strokeStyle =
        markupLayer.data.color || SCAN_PLAN_FALLBACK_LINE_COLOR

      markupLayer.data.rectPrimitives.forEach((primitive) => {
        appendScanPlanRectLines(
          GeckoComponentRect.CreateFromCoords(
            primitive.bounds.start,
            primitive.bounds.end
          ),
          bounds,
          strokeStyle,
          linePrimitives
        )
      })
    }

    if (isScanLabelLayerName(markupLayer.data.name)) {
      markupLayer.data.labelPrimitives.forEach((label) => {
        const overlayLabel = createScanPlanOverlayLabel(label, bounds)
        if (overlayLabel) {
          labelPrimitives.push(overlayLabel)
        }
      })
    }
  })

  return {
    linePrimitives,
    shapePrimitives: [],
    labelPrimitives,
  }
}

function getScanPlanOverlaySignature(primitives: OverlayPrimitives) {
  return JSON.stringify({
    lines: primitives.linePrimitives.map(({ x1, y1, x2, y2, strokeStyle }) => ({
      x1,
      y1,
      x2,
      y2,
      strokeStyle,
    })),
    labels: primitives.labelPrimitives.map(({ x, y, lines }) => ({
      x,
      y,
      lines,
    })),
  })
}

export const AssetDrawingTabContent: FC<AssetDrawingTabContentProps> = ({
  inspectionId,
  component,
  componentModel,
  thicknessCDF,
  markupLayers,
  metadataEntryId,
  onDrawingFinish,
  onCaptureStatusChange,
  isLocked,
  nominal,
  componentDefaults,
}) => {
  const { isLoading } = useRautReportOps()
  const { getDistanceUnit } = useUnitSystem()
  const { getPreferredMeasurementUnit } = useGeckoLocale()
  const { rolodexUploadFile, rolodexDeleteFile } = useRolodexEdit()
  const cdfDataFetcher = useCdfDataFetcher()
  const distanceUnit = getDistanceUnit()

  const [distancePerPage, setDistancePerPage] = useState(
    DEFAULT_DISTANCE_PER_PAGE
  )
  const [heatmapOverlay, setHeatmapOverlay] =
    useState<HeatmapOverlaySelection>('assetGeometry')
  const [loading, setLoading] = useState(false)
  const [captureStatus, setCaptureStatus] = useState<CaptureStatus>({
    isCapturing: false,
    message: '',
  })
  const [capturePercent, setCapturePercent] = useState(0)

  const { assetDrawings, forceRefresh: forceRefreshAssetDrawings } =
    useAssetDrawings(metadataEntryId, [inspectionId])
  const existingDrawings = useMemo<AssetDrawings>(
    () => assetDrawings.get(inspectionId) ?? { chunks: [] },
    [assetDrawings, inspectionId]
  )

  const thicknessDataFileQuery = useMemo(() => {
    const componentDatasetId = thicknessCDF?.tags['component-dataset']
    const path = thicknessCDF?.data.parquet_file_name

    if (!componentDatasetId || !path) {
      return undefined
    }

    return {
      entry_id: componentDatasetId,
      path,
    }
  }, [thicknessCDF])
  const {
    url: thicknessDataFileSignedUrl,
    isLoading: isLoadingThicknessDataFileSignedUrl,
  } = useRolodexSignedURLs(thicknessDataFileQuery)
  const isThicknessDataFileUnavailable =
    Boolean(thicknessCDF) &&
    !isLoadingThicknessDataFileSignedUrl &&
    (!thicknessDataFileQuery || !thicknessDataFileSignedUrl)

  const { fileRefs, forceRefresh: refreshFileList } = useRolodexFileLS({
    entry_id: metadataEntryId,
    path: `asset-screenshot/rautAssetDrawings/${inspectionId}/**`,
  })

  const colorThreshold = useMemo(() => {
    if (!thicknessCDF) return undefined

    const defaultsThreshold =
      componentDefaults?.data.color_scale_thresholds.get(
        thicknessCDF.data.params.view.id
      )
    if (defaultsThreshold) return defaultsThreshold

    return determineDefaultColorScaleThresholdNonCdf(
      thicknessCDF.data.params.view,
      thicknessCDF.data.params.measurement_unit,
      nominal
        ? GeckoMeasurementRange.CreateFromStartEnd(
            GeckoMeasurement.Zero(),
            nominal
          )
        : undefined
    )
  }, [componentDefaults, nominal, thicknessCDF])

  const relevantCdfLegendTitle = useMemo(() => {
    if (!thicknessCDF) return undefined

    return getCdfDisplayMetadata(
      thicknessCDF,
      getPreferredMeasurementUnit(thicknessCDF.data.params.measurement_unit)
    ).label
  }, [getPreferredMeasurementUnit, thicknessCDF])

  const overlayRects = useMemo(() => {
    const overlays: GeckoComponentRect[] = []

    markupLayers.filter(isReportMarkupLayer).forEach((markupLayer) => {
      if (isScanOverlayLayerName(markupLayer.data.name)) {
        overlays.push(
          ...markupLayer.data.rectPrimitives
            .map((primitive) =>
              GeckoComponentRect.CreateFromCoords(
                primitive.bounds.start,
                primitive.bounds.end
              )
            )
            .sort((left, right) => left.start.y.minus(right.start.y).num)
        )
      }
    })

    return overlays
  }, [markupLayers])

  const vizChunks = useMemo(() => {
    if (!thicknessCDF || overlayRects.length === 0) {
      return [toCartesianBounds(componentModel)]
    }

    const chunks: GeckoComponentRect[] = []
    const sortedScans = [...overlayRects].sort(
      (left, right) => left.start.y.minus(right.start.y).num
    )

    const cartesianBounds = toCartesianBounds(componentModel)
    const xMin = cartesianBounds.start.x.toNumber(distanceUnit)
    const xMax = cartesianBounds.end.x.toNumber(distanceUnit)

    let minY = sortedScans[0].start.y.toNumber(distanceUnit)
    let maxY = sortedScans[0].end.y.toNumber(distanceUnit)

    for (const scan of sortedScans) {
      minY = Math.min(minY, scan.start.y.toNumber(distanceUnit))
      maxY = Math.max(maxY, scan.end.y.toNumber(distanceUnit))
    }

    let currentPosition = minY

    while (currentPosition < maxY) {
      let chunkStart = currentPosition
      let chunkEnd = Math.min(currentPosition + distancePerPage, maxY)

      const intersectingBounds = getIntersectingScanBounds(
        sortedScans,
        chunkStart,
        chunkEnd,
        distanceUnit
      )

      if (intersectingBounds) {
        chunkStart = Math.min(chunkStart, intersectingBounds.minScanStart)
        chunkEnd = Math.max(chunkEnd, intersectingBounds.maxScanEnd)
      }

      chunks.push(
        GeckoComponentRect.CreateFromXYs(
          component.id,
          xMin,
          chunkStart,
          xMax,
          chunkEnd,
          distanceUnit,
          distanceUnit
        )
      )

      currentPosition = chunkEnd
    }

    return chunks
  }, [
    component,
    componentModel,
    distancePerPage,
    distanceUnit,
    overlayRects,
    thicknessCDF,
  ])

  const updateCaptureStatus = useCallback(
    (status: CaptureStatus) => {
      setCaptureStatus(status)
      onCaptureStatusChange?.(status)
    },
    [onCaptureStatusChange]
  )

  const captureAndSaveDrawings = useCallback(async () => {
    if (!thicknessCDF) {
      message.error('Unable to generate asset drawings without thickness data')
      return
    }
    if (isLoadingThicknessDataFileSignedUrl) {
      message.info('Checking thickness data file availability')
      return
    }
    if (!thicknessDataFileQuery || !thicknessDataFileSignedUrl) {
      message.error(
        'Unable to generate asset drawings - missing thickness data file'
      )
      return
    }

    setLoading(true)
    setCapturePercent(0)
    updateCaptureStatus({
      isCapturing: true,
      message: 'Preparing AssetViz renders...',
    })

    const uploadedPaths: string[] = []

    try {
      const nextChunks: RautInspectionAssetDrawingsData['chunks'] = {}

      for (const [index, geometry] of vizChunks.entries()) {
        const chunkKey = createRautAssetDrawingChunkKey(index)

        updateCaptureStatus({
          isCapturing: true,
          message: `Generating AssetViz render ${index + 1} of ${vizChunks.length}...`,
        })

        const buffer = await cdfDataFetcher(thicknessCDF, geometry)
        if (!buffer) {
          message.error('Unable to generate asset drawings - missing CDF data')
          return
        }

        if (!hasFiniteValues(buffer.values)) {
          throw new Error(
            `No finite CDF values found for ${thicknessCDF.id} in ${chunkKey}`
          )
        }
        const binSelection = gcrToBinSelection(geometry, thicknessCDF)
        const rawValuesXBinCount =
          binSelection && binSelection.x_end > binSelection.x_start
            ? binSelection.x_end - binSelection.x_start
            : undefined

        const scanPlanOverlayBounds = geometry.project(
          thicknessCDF.data.geometry.space
        )
        const scanPlanPrimitives = buildScanPlanOverlayPrimitives(
          markupLayers,
          scanPlanOverlayBounds
        )
        const scanPlanBundle = await renderReportHeatmapBundle({
          kind: 'section',
          componentId: component.id,
          sectionKey: `${chunkKey}-scan-plan`,
          cdfId: thicknessCDF.id,
          legendTitle: relevantCdfLegendTitle,
          cdf: thicknessCDF,
          componentModel,
          bounds: geometry,
          colorThreshold,
          includeAxes: false,
          includeComponentOverlay: true,
          componentOverlayMode: ReportComponentOverlayMode.Full,
          includeHeatmap: false,
          includeLegend: false,
          includeOverlay: true,
          overlayMode: ReportOverlayMode.Embedded,
          overlayPrimitives: scanPlanPrimitives,
          overlaySignature: getScanPlanOverlaySignature(scanPlanPrimitives),
          labelMode: ReportLabelMode.All,
          outputWidthPx: BUNDLE_OUTPUT_WIDTH_PX,
          background: 'white',
          rawValues: buffer.values,
          rawValuesXBinCount,
        })

        setCapturePercent(
          Math.round(((index * 2 + 1) / (vizChunks.length * 2)) * 100)
        )

        const includeHeatmapOverlay = heatmapOverlay !== 'none'
        const includeHeatmapScanPlanOverlay = heatmapOverlay === 'scanPlan'
        const bundle = await renderReportHeatmapBundle({
          kind: 'section',
          componentId: component.id,
          sectionKey: chunkKey,
          cdfId: thicknessCDF.id,
          legendTitle: relevantCdfLegendTitle,
          cdf: thicknessCDF,
          componentModel,
          bounds: geometry,
          colorThreshold,
          includeAxes: true,
          includeLegend: true,
          includeOverlay: includeHeatmapOverlay,
          includeComponentOverlay: includeHeatmapOverlay,
          componentOverlayMode: includeHeatmapOverlay
            ? ReportComponentOverlayMode.Full
            : undefined,
          overlayMode: includeHeatmapOverlay
            ? ReportOverlayMode.Embedded
            : ReportOverlayMode.None,
          overlayPrimitives: includeHeatmapScanPlanOverlay
            ? scanPlanPrimitives
            : undefined,
          overlaySignature: includeHeatmapScanPlanOverlay
            ? getScanPlanOverlaySignature(scanPlanPrimitives)
            : undefined,
          labelMode: includeHeatmapOverlay
            ? ReportLabelMode.All
            : ReportLabelMode.None,
          outputWidthPx: BUNDLE_OUTPUT_WIDTH_PX,
          background: 'white',
          rawValues: buffer.values,
          rawValuesXBinCount,
        })

        const paths = buildRautAssetDrawingBundlePaths({
          inspectionId,
          chunkKey,
          descriptorHash: `${bundle.descriptor.hash}-${scanPlanBundle.descriptor.hash}`,
        })

        await Promise.all([
          rolodexUploadFile(
            metadataEntryId,
            bundle.heatmapBlob,
            paths.imagePath
          ),
          rolodexUploadFile(
            metadataEntryId,
            scanPlanBundle.heatmapBlob,
            paths.scanPlanPath
          ),
          rolodexUploadFile(
            metadataEntryId,
            bundle.legendBlob,
            paths.legendPath
          ),
          rolodexUploadFile(
            metadataEntryId,
            new Blob(
              [
                serializeRautAssetDrawingDescriptor({
                  heatmap: bundle.descriptor,
                  scanPlan: scanPlanBundle.descriptor,
                }),
              ],
              {
                type: 'application/json',
              }
            ),
            paths.descriptorPath
          ),
        ])

        uploadedPaths.push(
          paths.imagePath,
          paths.scanPlanPath,
          paths.legendPath,
          paths.descriptorPath
        )

        nextChunks[chunkKey] = {
          bundlePath: paths.bundlePath,
          chunkKey,
          descriptorPath: paths.descriptorPath,
          imagePath: paths.imagePath,
          index,
          legendPath: paths.legendPath,
          scanPlanPath: paths.scanPlanPath,
          type: 'bundle',
        }

        setCapturePercent(Math.round(((index + 1) / vizChunks.length) * 100))
      }

      const uploadedPathSet = new Set(uploadedPaths)
      for (const fileRef of fileRefs) {
        if (!uploadedPathSet.has(fileRef.path)) {
          await rolodexDeleteFile(metadataEntryId, fileRef.path)
        }
      }

      await onDrawingFinish(inspectionId, { chunks: nextChunks })
      await refreshFileList()
      await forceRefreshAssetDrawings()

      message.success('AssetViz renders generated successfully')
    } catch (error) {
      console.error('Failed to generate AssetViz renders:', error)

      await Promise.allSettled(
        uploadedPaths.map((path) => rolodexDeleteFile(metadataEntryId, path))
      )

      message.error('Failed to generate asset drawings')
    } finally {
      setLoading(false)
      setCapturePercent(0)
      updateCaptureStatus({
        isCapturing: false,
        message: '',
      })
    }
  }, [
    cdfDataFetcher,
    colorThreshold,
    component,
    componentModel,
    fileRefs,
    forceRefreshAssetDrawings,
    heatmapOverlay,
    inspectionId,
    isLoadingThicknessDataFileSignedUrl,
    markupLayers,
    metadataEntryId,
    onDrawingFinish,
    refreshFileList,
    relevantCdfLegendTitle,
    rolodexDeleteFile,
    rolodexUploadFile,
    thicknessCDF,
    thicknessDataFileQuery,
    thicknessDataFileSignedUrl,
    updateCaptureStatus,
    vizChunks,
  ])

  return (
    <>
      {!thicknessCDF && (
        <Alert
          type='warning'
          showIcon
          message='Thickness data is required to generate AssetViz renders for this inspection.'
          style={{ marginBottom: '1rem' }}
        />
      )}
      {isThicknessDataFileUnavailable && (
        <Alert
          type='warning'
          showIcon
          message='The thickness data file is missing for this inspection. Select another component or publish the missing component dataset files before generating AssetViz renders.'
          style={{ marginBottom: '1rem' }}
        />
      )}

      {overlayRects.length > 0 && (
        <ZoomControls>
          <AntText>Distance Per Page (in {distanceUnit.name}):</AntText>
          <InputNumber
            min={0}
            step={1}
            value={distancePerPage}
            onChange={(value) => {
              if (value) {
                setDistancePerPage(value)
              }
            }}
            disabled={isLocked || loading}
          />
          <Button
            size='small'
            onClick={() => setDistancePerPage(DEFAULT_DISTANCE_PER_PAGE)}
            disabled={isLocked || loading}>
            Reset
          </Button>
        </ZoomControls>
      )}

      {thicknessCDF && (
        <ZoomControls>
          <AntText>Heatmap Overlay:</AntText>
          <Select
            style={{ width: 260 }}
            value={heatmapOverlay}
            onChange={setHeatmapOverlay}
            options={HEATMAP_OVERLAY_OPTIONS}
            disabled={isLocked || loading}
          />
        </ZoomControls>
      )}

      <ButtonContainer>
        <Button
          onClick={captureAndSaveDrawings}
          loading={loading}
          disabled={
            isLocked ||
            isLoading ||
            isLoadingThicknessDataFileSignedUrl ||
            !thicknessCDF ||
            isThicknessDataFileUnavailable
          }>
          {existingDrawings.chunks.length > 0
            ? 'Regenerate Asset Drawings'
            : 'Generate Asset Drawings'}
        </Button>
      </ButtonContainer>

      {loading && (
        <StatusCard>
          <ProgressInfo>
            <div>
              <strong>Status:</strong> {captureStatus.message}
            </div>
            <Progress percent={capturePercent} status='active' />
          </ProgressInfo>
        </StatusCard>
      )}

      {existingDrawings.chunks.length > 0 &&
        existingDrawings.chunks.map((chunk) => (
          <Card
            key={chunk.chunkKey}
            title={
              existingDrawings.chunks.length > 1
                ? `AssetViz Render ${chunk.index + 1}`
                : 'AssetViz Render'
            }>
            {chunk.scanPlanSignedUrl && (
              <>
                <AntText strong>Scan Plan</AntText>
                <div style={{ marginBottom: 12 }}>
                  <AntImage
                    src={chunk.scanPlanSignedUrl}
                    alt={`AssetViz scan plan ${chunk.index + 1}`}
                    style={{ display: 'block', width: '100%' }}
                  />
                </div>
              </>
            )}
            <AntText strong>Thickness Heatmap</AntText>
            <PreviewRow>
              <div style={{ flex: 1, minWidth: 0 }}>
                <AntImage
                  src={chunk.signedUrl}
                  alt={`AssetViz render ${chunk.index + 1}`}
                  style={{ display: 'block', width: '100%' }}
                />
              </div>
              {chunk.legendSignedUrl && (
                <AntImage
                  src={chunk.legendSignedUrl}
                  alt={`AssetViz legend ${chunk.index + 1}`}
                  style={{ width: '72px' }}
                />
              )}
            </PreviewRow>
          </Card>
        ))}
    </>
  )
}
